// ─── Event type discriminants ────────────────────────────────────────

/** Short type codes for input events sent over the data channel. */
export type InputEventType = "mm" | "mr" | "md" | "mu" | "dc" | "kd" | "ku" | "wh";

// ─── Modifier flags ─────────────────────────────────────────────────

/** Bitmask values for modifier keys (fits in a single byte). */
export const Modifier = {
  SHIFT: 0x01,
  CTRL: 0x02,
  ALT: 0x04,
  META: 0x08,
} as const;

/** Modifier bitmask (OR of Modifier values). */
export type ModifierMask = number;

// ─── Event payloads ─────────────────────────────────────────────────

/** Base fields present in every input event. */
interface InputEventBase {
  ts: number;
  seq: number;
}

/** Mouse move event. Coordinates normalised to VM resolution. */
export interface MouseMoveEvent extends InputEventBase {
  t: "mm";
  x: number;
  y: number;
}

/** Mouse button press/release/double-click. */
export interface MouseButtonEvent extends InputEventBase {
  t: "md" | "mu" | "dc";
  x: number;
  y: number;
  btn: number;
}

/** Keyboard key press/release. */
export interface KeyEvent extends InputEventBase {
  t: "kd" | "ku";
  key: string;
  code: string;
  mod: ModifierMask;
}

/** Scroll wheel event. */
export interface WheelEvent extends InputEventBase {
  t: "wh";
  x: number;
  y: number;
  dx: number;
  dy: number;
}

/** Relative mouse movement (pointer lock mode — FPS games). */
export interface MouseMoveRelativeEvent extends InputEventBase {
  t: "mr";
  dx: number;
  dy: number;
}

/** Union of all input events that can be sent to the VM. */
export type InputEvent =
  | MouseMoveEvent
  | MouseMoveRelativeEvent
  | MouseButtonEvent
  | KeyEvent
  | WheelEvent;

// ─── Configuration ──────────────────────────────────────────────────

/** VM display resolution used for coordinate normalisation. */
export interface VmResolution {
  width: number;
  height: number;
}

/** Numeric type codes for binary serialisation (1 byte). */
export const BinaryTypeCode: Record<InputEventType, number> = {
  mm: 0,
  mr: 7,
  md: 1,
  mu: 2,
  dc: 6,
  kd: 3,
  ku: 4,
  wh: 5,
} as const;

/** Reverse lookup: numeric code → event type string. */
export const BinaryTypeFromCode: Record<number, InputEventType> = {
  0: "mm",
  1: "md",
  2: "mu",
  3: "kd",
  4: "ku",
  5: "wh",
  6: "dc",
  7: "mr",
} as const;

/** Size of the compact binary frame in bytes. */
export const BINARY_FRAME_SIZE = 15;
